import { convertObjectKeysToPascalCase } from './utilities';

/** In-memory JWT for self-serve API Gateway (issued by hgpro-web session endpoint). */
let selfServeApiToken = null;

/** @param {string|null} token */
export const setSelfServeApiToken = (token) => {
  selfServeApiToken = token || null;
};

export const getSelfServeApiToken = () => selfServeApiToken;

/**
 * Get the HGPro base URL based on environment variable or hostname
 * @param {string} hostname - The hostname to check
 * @returns {string} The base URL for HGPro API
 */
export const getHgproBaseurl = (hostname) => {
  if (process.env.HGPRO_BASE_URL) {
    return process.env.HGPRO_BASE_URL;
  }

  // Fall back to hostname-based logic
  switch (true) {
    case hostname.includes('localhost'):
      // TEMPORARY: Using team environment instead of localhost for development
      // The team env doesn't require auth (unlike localhost) which speeds up development
      // TODO: Replace with environment variable to toggle between local/team environments
      // return 'http://localhost:39912'; // Local dev environment
      // return 'https://hgpro-team-gop.healthgrades.zone'; // Team env (no auth required)
      return 'http://localhost:39912';
    case hostname.includes('elevated-profiles-team.healthgrades.zone'):
      return 'https://hgpro-team-gop.healthgrades.zone';
    case hostname.includes('selfservetest'):
      return 'https://update-alpha-testaws.healthgrades.com';
    default:
      return 'https://update.healthgrades.com';
  }
};

/** Shared query for self-serve register/login hand-off (MD-2924 eligibility gate). */
export function buildSelfServeAuthQueryString(
  redirectOrigin = window.location.origin,
) {
  const redirectUri =
    typeof window !== 'undefined' && window.location.href
      ? window.location.href.split('#')[0]
      : redirectOrigin;

  const params = new URLSearchParams({
    fromSource: 'self-serve',
    redirect_uri: redirectUri,
    pCID: 'self-serve',
  });
  return params.toString();
}

/**
 * When true, self-serve register targets hgpro-nextjs (not login — login always
 * starts on legacy; hgpro-web RedirectAnonymousSignInToNextMiddleware sends
 * anonymous GET /account/sign-in to Next /login when LoginRedirectEnabled is on).
 * Mirrors hgpro-web HgProNext:RoleBasedRouting:SelfServeRedirectToNextEnabled.
 */
function isSelfServeAuthRedirectToNextEnabled() {
  const raw = process.env.HGPRO_SELF_SERVE_REDIRECT_TO_NEXT_ENABLED ?? '';
  return String(raw).trim().toLowerCase() === 'true';
}

/** Register URL with self-serve context for post-register eligibility. */
export function buildSelfServeRegisterUrl(hgproBaseUrl) {
  const base = isSelfServeAuthRedirectToNextEnabled()
    ? getHgproNextPortalBaseUrl(window.location.hostname)
    : hgproBaseUrl;
  return `${base}/account/register?${buildSelfServeAuthQueryString()}`;
}

/**
 * Login URL with self-serve context for post-login eligibility.
 * Always legacy `/account/sign-in` on HGPRO_BASE_URL; hgpro-web middleware
 * redirects to Next `/login` when HgProNext:RoleBasedRouting:LoginRedirectEnabled
 * is true (no webpack mirror flag required on self-serve-ui).
 */
export function buildSelfServeLoginUrl(hgproBaseUrl) {
  return `${hgproBaseUrl}/account/sign-in?${buildSelfServeAuthQueryString()}`;
}

/** hgpro-web self-serve sign-out API (clears HGPro session cookies). */
export function buildSelfServeSignOutUrl(hgproBaseUrl) {
  return `${hgproBaseUrl}/api/account/self-serve-sign-out`;
}

/**
 * @param {string} url - URL to fetch
 * @param {string} [apiVersion] - API version to include in the headers
 */
export const get = async (url, apiVersion = '') => {
  return fetch(url, {
    headers: getHeader(apiVersion),
    credentials: 'include',
  }).then((res) => status(res, url));
};

/**
 * @param {string} url - URL to post to
 * @param {any} body - Request body
 * @param {string} [apiVersion] - API version to include in the headers
 */
export const post = async (url, body, apiVersion = '') => {
  return fetch(url, {
    method: 'POST',
    headers: getHeader(apiVersion),
    body: JSON.stringify(body),
    credentials: 'include',
  }).then((res) => status(res, url));
};

/**
 * Get headers for API requests
 * @param {string} [apiVersion] - API version to include in the headers
 */
const getHeader = (apiVersion = '') => {
  const headers = {
    'Content-Type': 'application/json',
  };

  if (apiVersion) {
    headers['api-version'] = apiVersion;
  }

  if (selfServeApiToken) {
    headers.Authorization = `Bearer ${selfServeApiToken}`;
  }

  return headers;
};

/** Headers for legacy direct fetch calls to the self-serve Lambda API. */
export const getSelfServeRequestHeaders = () => getHeader();

const status = (res, requestUrl = '') => {
  return new Promise((resolve, reject) => {
    if (res.status === 401) {
      setSelfServeApiToken(null);
      const isSelfServeLambdaApi =
        typeof requestUrl === 'string' &&
        (requestUrl.includes('apigwdev.healthgrades.com') ||
          requestUrl.includes('apigw.healthgrades.com') ||
          requestUrl.includes('localhost:44339'));
      if (!isSelfServeLambdaApi) {
        window.location.replace('/');
      } else {
        reject({ error: 'Unauthorized', status: 401 });
      }
      return;
    } else if (res.status >= 200 && res.status < 300) {
      res
        .json()
        .then((data) => resolve(data))
        .catch((error) => {
          // If JSON parsing fails due to empty response, resolve with null
          // This is common for endpoints like keep-alive that return 200 with no body
          if (
            error.message.includes('Unexpected end of JSON input') ||
            error.message.includes('JSON')
          ) {
            resolve(null);
          } else {
            reject(error);
          }
        });
    } else if (res.status === 500 || res.status === 404) {
      reject({ error: 'Error Processing Request' });
    } else {
      res.json().then((parsedResponse) => {
        reject(parsedResponse);
      });
    }
  });
};

/**
 * Get the HGMD base URL based on environment variable or hostname
 * @returns {string} The base URL for HGMD
 */
export const hgmdBaseurl = () => {
  if (process.env.HGPRO_BASE_URL) {
    return process.env.HGPRO_BASE_URL;
  }

  // Fall back to hostname-based logic
  return window.location.hostname.indexOf('selfservetest') > -1 ||
    window.location.hostname.indexOf('localhost') > -1
    ? 'https://update-alpha-testaws.healthgrades.com/'
    : 'https://update.healthgrades.com/';
};

export const baseurl = () => {
  if (process.env.SELF_SERVE_LAMBDA_BASE_URL) {
    return process.env.SELF_SERVE_LAMBDA_BASE_URL;
  }

  const hostname = window.location.hostname;

  switch (true) {
    case hostname.indexOf('localhost') > -1:
      return 'https://localhost:44339';
    case hostname.indexOf('selfservetest') > -1:
      return 'https://apigwdev.healthgrades.com/selfserve';
    default:
      return 'https://apigw.healthgrades.com/selfserve';
  }
};

/**
 * Next.js provider portal base URL for self-serve checkout hand-off (MD-2924).
 */
export const getHgproNextPortalBaseUrl = (hostname) => {
  if (process.env.HGPRO_NEXT_PORTAL_BASE_URL) {
    return process.env.HGPRO_NEXT_PORTAL_BASE_URL;
  }

  switch (true) {
    case hostname.includes('localhost'):
      return 'http://localhost:3000';
    case hostname.includes('selfservetest'):
      return 'https://update-next-testaws.healthgrades.com';
    default:
      return 'https://update-v2.healthgrades.com';
  }
};

/**
 * Reads MD-2924 self-serve provider post-login redirect flag (explicit opt-in).
 */
function isSelfServeProviderPostLoginRedirectEnabled() {
  const raw =
    process.env.HGPRO_SELF_SERVE_PROVIDER_POST_LOGIN_REDIRECT_ENABLED ?? '';
  return String(raw).trim().toLowerCase() === 'true';
}

/**
 * Post-Stripe checkout redirect for self-serve (MD-2924).
 * Elevated Profile Admin → Next elevated roster when flag on (else legacy);
 * Provider → Next dashboard when flag on (else legacy).
 */
export const buildSelfServeCheckoutSuccessRedirectUrl = (
  hostname,
  { isAdmin, isProvider, sessionId },
) => {
  const params = new URLSearchParams({
    fromSource: 'self-serve-success',
    session_id: sessionId,
  });
  const qs = params.toString();
  if (isAdmin && isSelfServeProviderPostLoginRedirectEnabled()) {
    return `${getHgproNextPortalBaseUrl(hostname)}/elevated-roster?${qs}`;
  }
  if (isAdmin) {
    return `${getHgproBaseurl(hostname)}/elevatedRoster?${qs}`;
  }
  if (isProvider && isSelfServeProviderPostLoginRedirectEnabled()) {
    return `${getHgproNextPortalBaseUrl(hostname)}/dashboard?${qs}`;
  }
  if (isProvider) {
    return `${getHgproBaseurl(hostname)}/dashboard/index?${qs}`;
  }
  return `${getHgproBaseurl(hostname)}?${qs}`;
};

/** External Help Center URL (same for legacy and Next provider nav). */
const SELF_SERVE_HELP_CENTER_URL =
  'https://helpcenter.healthgrades.com/help?utm_source=hgmd&utm_medium=footer&utm_campaign=help-center';

/**
 * Profile menu hrefs for authenticated self-serve header (MD-2924).
 * Provider + post-login redirect flag → Next portal; Admin/Roster → legacy HGPro.
 *
 * @param {string} hostname
 * @param {{ isProvider: boolean }} options
 * @returns {{ helpCenter: string, accountSettings: string, changePassword: string }}
 */
export function buildSelfServeProfileMenuUrls(hostname, { isProvider }) {
  const legacyBase = getHgproBaseurl(hostname);
  const useNextPortal =
    isProvider && isSelfServeProviderPostLoginRedirectEnabled();
  const profileBase = useNextPortal
    ? getHgproNextPortalBaseUrl(hostname)
    : legacyBase;

  return {
    helpCenter: SELF_SERVE_HELP_CENTER_URL,
    accountSettings: `${profileBase}/account/settings`,
    changePassword: `${profileBase}/account/${useNextPortal ? 'changepassword' : 'ChangePassword'}`,
  };
}

/**
 * Dashboard URL that opens the self-serve ineligible modal (`fromSource=self-serve-failure`).
 * Used when a Provider opens self-serve directly with an existing session and is not eligible.
 */
export const buildSelfServeIneligibleDashboardRedirectUrl = (hostname) => {
  const params = new URLSearchParams({
    fromSource: 'self-serve-failure',
  });
  const qs = params.toString();
  if (isSelfServeProviderPostLoginRedirectEnabled()) {
    return `${getHgproNextPortalBaseUrl(hostname)}/dashboard?${qs}`;
  }
  return `${getHgproBaseurl(hostname)}/dashboard/index?${qs}`;
};

/**
 * Turns hgpro-web redirect paths into absolute URLs for cross-origin navigation.
 * Absolute API URLs (e.g. Next dashboard) are returned as-is; relative paths are
 * prefixed with the legacy HGPro base URL.
 */
export const resolveSelfServeIneligibleRedirectUrl = (
  apiRedirectUrl,
  hostname = typeof window !== 'undefined' ? window.location.hostname : '',
) => {
  const fallback = buildSelfServeIneligibleDashboardRedirectUrl(hostname);
  if (!apiRedirectUrl?.trim()) {
    return fallback;
  }

  const trimmed = apiRedirectUrl.trim();
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) {
    return trimmed;
  }

  const base = getHgproBaseurl(hostname).replace(/\/$/, '');
  const path = trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
  return `${base}${path}`;
};

const _baseurl = baseurl();

export const storeSession = async (sessionData) => {
  const sessionDataInPascalCase = convertObjectKeysToPascalCase(sessionData);
  try {
    const response = await fetch(`${_baseurl}/SelfService/store-session`, {
      method: 'POST',
      headers: getSelfServeRequestHeaders(),
      body: JSON.stringify(sessionDataInPascalCase),
    });
    if (!response.ok) {
      console.log(`Session store failed with status ${response.status}`);
    }
    const { sid } = await response.json();
    return sid;
  } catch (error) {
    console.error('Failed to store session:', error);
  }
};

export const UpdateSession = async (sessionId) => {
  try {
    const response = await fetch(
      `${_baseurl}/SelfService/update-session?sid=${sessionId}`,
      {
        method: 'POST',
        headers: getSelfServeRequestHeaders(),
      },
    );
    if (!response.ok) {
      console.log(`Request failed with status ${response.status}`);
    }
    const { sid } = await response.json();
    return sid;
  } catch (error) {
    console.error('Failed to update session', error);
  }
};

export const getSession = async (sid, pwid) => {
  try {
    const response = await fetch(
      `${_baseurl}/SelfService/get-session?sid=${sid}`,
      { headers: getSelfServeRequestHeaders() },
    );
    if (!response.ok) {
      console.log(`Request failed with status ${response.status}`);
    }
    const data = await response.json();
    const val = pwid ? await checkSessionStatus(pwid) : false;
    const _data = { ...data, isLoggedIn: val };
    return _data;
  } catch (error) {
    console.error('Failed to retrieve session', error);
  }
};

export async function checkSessionStatus(pwid) {
  const _url = getHgproBaseurl(window.location.hostname);
  const errorResponse = false;
  try {
    const response = await get(
      `${_url}/api/account/validateSessionForSelfServe?pwid=${encodeURIComponent(pwid)}`,
    );
    return response;
  } catch (error) {
    LogErrorMessage(error);
    return errorResponse;
  }
}
export const LogErrorMessage = async (errorMessage) => {
  try {
    const response = await fetch(`${_baseurl}/SelfService/LogUiError`, {
      method: 'POST',
      headers: getSelfServeRequestHeaders(),
      body: JSON.stringify(errorMessage),
    });
    if (!response.ok) {
      console.log(
        `Logging error to service failed with status code ${response.status}`,
      );
    }
    return response;
  } catch (error) {
    console.error('Failed to log the error:', error);
  }
};
